import React, { useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'utils/hooks';

import Button from 'components/shared/Button';
import Spinner from 'components/shared/Spinner';

import Flyout from 'components/features/ProductSidebar/Flyout';
import Subcatalogs from 'components/features/ProductSidebar/Subcatalogs';
import Products from 'components/features/ProductSidebar/Products';
import OptionGroup from 'components/features/ProductSidebar/OptionGroup';
import ChangeCatalogButton from 'components/features/ProductSidebar/ChangeCatalogButton';
import MobileNavigation from 'components/features/MobileNavigation';

import AddToCartWrapper from 'components/features/AddToCart/AddToCartWrapper';

import {
  selectOptionsLoaded,
  selectOptionsLoading,
  selectSubcatalogsLoaded,
  selectSubcatalogsLoading,
} from 'selectors/loading_states';
import ui from 'domains/ui';
import activeEditor from 'domains/active_editor';

function ProductSidebar() {
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [leaveFlyoutOpen, setLeaveFlyoutOpen] = useState(false);

  const sidebarRef = useRef(null);

  const dispatch = useAppDispatch();
  const activeCatalog = useAppSelector(activeEditor.selectors.catalog);
  const activeSubcatalog = useAppSelector(activeEditor.selectors.subcatalog);
  const subcatalogsLoading = useAppSelector(selectSubcatalogsLoading);
  const subcatalogsLoaded = useAppSelector(selectSubcatalogsLoaded);
  const optionsLoading = useAppSelector(selectOptionsLoading);
  const optionsLoaded = useAppSelector(selectOptionsLoaded);
  const optionGroups = useAppSelector(activeEditor.selectors.optionGroups);
  const hasMobileNavigation = useAppSelector(ui.selectors.hasMobileNavigation);
  const showMobileSidebar = useAppSelector(ui.selectors.showMobileSidebar);

  const subcatalogName = useMemo(
    () => (activeSubcatalog ? activeSubcatalog.name : null),
    [activeSubcatalog]
  );

  const subcatalogLabel = useMemo(
    () => (activeCatalog ? activeCatalog.label_ui : null),
    [activeCatalog]
  );

  const subcatalogDescription = useMemo(
    () => (activeSubcatalog ? activeSubcatalog.tooltip_description : null),
    [activeSubcatalog]
  );

  const heading = useMemo(() => {
    return subcatalogLabel || subcatalogName || '';
  }, [subcatalogName, subcatalogLabel]);

  if (!activeCatalog || !activeSubcatalog) return null;
  return (
    <div className={`sidebar-wrapper ${showMobileSidebar ? 'reveal' : ''}`}>
      <ChangeCatalogButton />
      <MobileNavigation />
      <div ref={sidebarRef} className={`sidebar${hasMobileNavigation ? ' mobile' : ''}`}>
        <div className="sidebar_content">
          {subcatalogsLoading && (
            <div className="spinner_container">
              <Spinner size="100" />
            </div>
          )}
          {subcatalogsLoaded && (
            <React.Fragment>
              <Flyout
                heading={heading}
                subheading={subcatalogLabel ? subcatalogName : null}
                description={subcatalogDescription}
                setFlyoutOpen={setFlyoutOpen}
                flyoutOpen={flyoutOpen}
                leaveFlyoutOpen={leaveFlyoutOpen}
              >
                <Subcatalogs
                  setFlyoutOpen={setFlyoutOpen}
                  setLeaveFlyoutOpen={setLeaveFlyoutOpen}
                />
              </Flyout>
              <Products />
            </React.Fragment>
          )}
          {optionsLoading && (
            <div className="spinner_container">
              <Spinner size="50" />
            </div>
          )}
          {optionsLoaded &&
            optionGroups.map((optionGroup) => (
              <OptionGroup key={optionGroup.id} optionGroup={optionGroup} />
            ))}
        </div>
        <Button
          type="icon"
          icon="times"
          className="close"
          size="2x"
          appearance="light"
          a11yText="Close Menu"
          onClick={() => dispatch(ui.actions.setShowMobileSidebar(false))}
        />
      </div>
      <AddToCartWrapper />
    </div>
  );
}

export default ProductSidebar;
